import type { SessionResponse } from '@fuel-system/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '@/lib/api-client';

/** This app is the `operator` Unified Access platform. */
const APP_SLUG = 'operator';

/** The Unified Access web app this platform trusts. */
export function uaPortalUrl(): string {
  return (import.meta.env['VITE_UA_PORTAL_URL'] as string | undefined) ?? 'http://localhost:5200';
}

/**
 * unified-access.md section 3, step 5 and ui-contract.md section 6: sends the
 * browser to Unified Access with the current absolute page as `returnTo`, so
 * the employee lands back on the same page after signing in again.
 */
export function redirectToSignIn(): void {
  const returnTo = encodeURIComponent(globalThis.location.href);
  globalThis.location.href = `${uaPortalUrl()}/login?returnTo=${returnTo}`;
}

export const SESSION_QUERY_KEY = ['session'] as const;

/**
 * Cookie session, so there is no client-held token to branch on: GET
 * /auth/operator/me is the source of truth. A 401 means "no session"; every
 * other failure is a real error the caller should show.
 */
export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => apiRequest<SessionResponse>(`/auth/${APP_SLUG}/me`),
    retry: false,
    staleTime: 60_000,
  });
}

export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 401;
}

/**
 * Redeems a launch code from Unified Access, opening the app's own session
 * cookie server-side.
 */
export function useLaunch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiRequest<SessionResponse>(`/auth/${APP_SLUG}/launch`, { method: 'POST', body: { code } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });
}

/**
 * Signing out of this app clears its own session cookie, then sends the
 * browser to Unified Access; a true cross-app sign-out needs a UA-side change
 * and is out of this platform's scope.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>(`/auth/${APP_SLUG}/logout`, { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
      globalThis.location.href = `${uaPortalUrl()}/logout`;
    },
  });
}
